import { useMemo } from "react";
import flatten from "lodash/flatten";
import type { UseInfiniteQueryOptions } from "react-query";
import { useInfiniteQuery } from "react-query";
import { useWorkflowsApi } from "@circle-react/api";
import type { ListWorkflows } from "@circle-react/components/SettingsApp/Workflows/generated/WorkflowsApi.types";
import type { Params } from "@circle-react/helpers/urlHelpers";

export const SORT_OPTIONS_MAP = {
  ALPHABETICAL: "alphabetical",
  OLDEST: "oldest",
  LATEST: "latest",
} as const;

export const DEFAULT_PAGE_SIZE = 20 as const;

interface UseBotWorkflowsQueryArgs {
  agentId: number;
  initialPage?: number;
  options?: UseInfiniteQueryOptions<ListWorkflows>;
  perPage?: number;
  query?: string;
  sort?: (typeof SORT_OPTIONS_MAP)[keyof typeof SORT_OPTIONS_MAP];
}

export const useBotWorkflowsQuery = (args: UseBotWorkflowsQueryArgs) => {
  const {
    agentId,
    query = "",
    sort = SORT_OPTIONS_MAP.LATEST,
    perPage = DEFAULT_PAGE_SIZE,
    initialPage = 1,
    options = {},
  } = args;

  const workflowsApi = useWorkflowsApi();

  const fetchPage = ({ pageParam = initialPage }) => {
    let page = 1;

    if (typeof pageParam === "number" && pageParam > 0) {
      page = pageParam;
    }

    const params: Params = {
      page,
      sort,
      per_page: perPage,
      q: {
        actions_action_type_in: [
          "ai_agents_post_comment",
          "ai_agents_report_comment",
          "ai_agents_report_post",
          "ai_agents_hide_post_from_featured_areas",
        ],
        actions_community_bot_profile_id_eq: agentId,
      },
    };

    if (query) {
      params.query = query;
    }

    return workflowsApi.list({ params });
  };

  const queryKey = [
    "bot-workflows",
    agentId,
    query,
    sort,
    perPage,
    initialPage,
  ];

  const queryOptions = {
    getNextPageParam: (currentPage: ListWorkflows) =>
      currentPage.has_next_page && currentPage.page
        ? currentPage.page + 1
        : undefined,
    getPreviousPageParam: (currentPage: ListWorkflows) =>
      currentPage.page && currentPage.page > 1
        ? currentPage.page - 1
        : undefined,
    keepPreviousData: true,
    ...options,
  };

  const result = useInfiniteQuery<ListWorkflows>(
    queryKey,
    fetchPage,
    queryOptions,
  );

  const pages = useMemo(() => result.data?.pages || [], [result.data?.pages]);
  const totalWorkflows = pages[0]?.total_count || 0;
  const pageSize = pages[0]?.per_page || perPage;
  const workflows = useMemo(
    () => flatten(pages.map(page => page.records)),
    [pages],
  );

  return {
    ...result,
    queryKey,
    totalWorkflows,
    workflows,
    pageSize,
    pages,
    hasWorkflows: totalWorkflows > 0,
  };
};

export const useBotHasWorkflows = (agentId: number) => {
  const { totalWorkflows, isLoading } = useBotWorkflowsQuery({
    agentId,
    perPage: 1,
  });

  return {
    hasWorkflows: totalWorkflows > 0,
    isLoading,
  };
};
